(function() {
  'use strict';

  // ── Internationale referentietabellen (EN 13402 / ISO) ──────────────────
  const INT_SIZES = {
    women: [
      { bust: 76, waist: 58, hip: 82,  eu: 32, uk: 6,  us: 0,  fr: 34, it: 36 },
      { bust: 80, waist: 62, hip: 86,  eu: 34, uk: 8,  us: 2,  fr: 36, it: 38 },
      { bust: 84, waist: 66, hip: 90,  eu: 36, uk: 10, us: 4,  fr: 38, it: 40 },
      { bust: 88, waist: 70, hip: 94,  eu: 38, uk: 12, us: 6,  fr: 40, it: 42 },
      { bust: 92, waist: 74, hip: 98,  eu: 40, uk: 14, us: 8,  fr: 42, it: 44 },
      { bust: 96, waist: 78, hip: 102, eu: 42, uk: 16, us: 10, fr: 44, it: 46 },
      { bust: 100,waist: 82, hip: 106, eu: 44, uk: 18, us: 12, fr: 46, it: 48 },
      { bust: 104,waist: 86, hip: 110, eu: 46, uk: 20, us: 14, fr: 48, it: 50 },
    ],
    men: [
      { chest: 88,  waist: 74, eu: 44, uk: 34, us: 34 },
      { chest: 92,  waist: 78, eu: 46, uk: 36, us: 36 },
      { chest: 96,  waist: 82, eu: 48, uk: 38, us: 38 },
      { chest: 100, waist: 86, eu: 50, uk: 40, us: 40 },
      { chest: 104, waist: 90, eu: 52, uk: 42, us: 42 },
      { chest: 108, waist: 94, eu: 54, uk: 44, us: 44 },
    ]
  };

  // ── Reference sizing baseline (EN 13402 / international) ─────────────────
  const GBT_CHEST = {
    women: { XS: 82, S: 86, M: 90, L: 94, XL: 98, XXL: 102, '3XL': 106 },
    men:   { XS: 88, S: 92, M: 96, L: 100, XL: 104, XXL: 108, '3XL': 112 }
  };

  // ── Stretch-tolerantie voor lichaamsmaten (hoeveel cm boven maattabel mag) ──
  // Bij stretch-stof mag klant iets boven de chart-waarde zitten en past het nog.
  const STRETCH_TOLERANCE = { none: 0, low: 2, medium: 4, high: 6 };

  const META_KEYS = new Set(['garment_category','fit_type','fabric_stretch','primary_measurement','gender']);
  const SCORABLE  = new Set(['chest_cm','bust_cm','waist_cm','hip_cm']);
  const BODY_KEY  = { chest_cm: 'chest', bust_cm: 'chest', waist_cm: 'waist', hip_cm: 'hip' };

  // ── Canonical size ordering + aliases ────────────────────────────────────
  const SIZE_ORDER = ['XXS','XS','S','M','L','XL','XXL','3XL','4XL','5XL'];
  const SIZE_ALIAS = { '2XL': 'XXL', 'XXXL': '3XL', 'XXXXL': '4XL' };

  function sizeIndex(label) {
    const upper = SIZE_ALIAS[label.toUpperCase()] || label.toUpperCase();
    const idx = SIZE_ORDER.indexOf(upper);
    if (idx !== -1) return idx;
    const num = parseFloat(label);
    return !isNaN(num) ? 100 + num : 999;
  }

  // ── Extrapolate missing sizes via linear interpolation/extrapolation ────
  function extrapolateChart(chart, productSizes) {
    if (!productSizes || !productSizes.length) return new Set();

    // Collect known sizes sorted by canonical index
    const known = [];
    for (const [lbl, val] of Object.entries(chart)) {
      if (META_KEYS.has(lbl) || typeof val !== 'object') continue;
      known.push({ label: lbl, idx: sizeIndex(lbl), data: val });
    }
    if (known.length < 2) return new Set(); // need 2+ points
    known.sort((a, b) => a.idx - b.idx);

    // All measurement keys from known sizes
    const measureKeys = [...new Set(known.flatMap(k => Object.keys(k.data)))];

    // Lookup by canonical index
    const knownByIdx = new Map(known.map(k => [k.idx, k]));
    const knownLabels = new Set(known.map(k => k.label));

    const extrapolated = new Set();

    for (const size of productSizes) {
      if (knownLabels.has(size)) continue;
      const targetIdx = sizeIndex(size);
      if (targetIdx === 999) continue; // unrecognized format
      if (knownByIdx.has(targetIdx)) continue; // alias already exists

      const measurements = {};

      for (const key of measureKeys) {
        // Find nearest known below and above that have this measurement
        let below = null, above = null;
        for (const k of known) {
          if (k.data[key] == null) continue;
          if (k.idx <= targetIdx) below = k;
          if (k.idx >= targetIdx && !above) above = k;
        }

        let val = null;
        if (below && above && below !== above) {
          // INTERPOLATION: between two known points
          const t = (targetIdx - below.idx) / (above.idx - below.idx);
          val = below.data[key] + (above.data[key] - below.data[key]) * t;
        } else if (below) {
          // EXTRAPOLATION UP: from the two largest known points
          const pair = known.filter(k => k.data[key] != null).slice(-2);
          if (pair.length === 2) {
            const step = (pair[1].data[key] - pair[0].data[key]) / (pair[1].idx - pair[0].idx);
            val = pair[1].data[key] + step * (targetIdx - pair[1].idx);
          }
        } else if (above) {
          // EXTRAPOLATION DOWN: from the two smallest known points
          const pair = known.filter(k => k.data[key] != null).slice(0, 2);
          if (pair.length === 2) {
            const step = (pair[1].data[key] - pair[0].data[key]) / (pair[1].idx - pair[0].idx);
            val = pair[0].data[key] + step * (targetIdx - pair[0].idx);
          }
        }

        if (val !== null) measurements[key] = Math.max(0, Math.round(val));
      }

      if (Object.keys(measurements).length > 0) {
        chart[size] = measurements;
        extrapolated.add(size);
      }
    }

    return extrapolated;
  }

  // Labels komen uit de taalbestanden via window.__scLabels__ (gezet in
  // stilenza-size-guide.liquid). Stonden ze hier hard, dan kreeg een Japanse koper
  // zijn maatadvies met Engelse kolomkoppen — precies het detail waarop die markt
  // een winkel als buitenlands wegzet. De Engelse waarden blijven als terugval.
  const T = (window.__scLabels__ || {});
  const LABEL_MAP = {
    chest_cm: T.chest || 'Chest',
    bust_cm: T.bust || 'Bust',
    waist_cm: T.waist || 'Waist',
    hip_cm: T.hip || 'Hip',
    length_cm: T.length || 'Length',
    shoulder_cm: T.shoulder || 'Shoulder',
    sleeve_cm: T.sleeve || 'Sleeve',
    foot_length_cm: T.foot || 'Foot length',
    insole_cm: T.insole || 'Insole',
    heel_cm: T.heel || 'Heel height',
    eu: 'EU', uk: 'UK', us: 'US'
  };

  const STORAGE_KEY = 'sc_body_v1';

  // ── Score voor lichaamsmaten ─────────────────────────────────────────────
  // diff = chartWaarde - lichaamswaarde
  //   positief: klant zit onder de maatgrens → past goed
  //   negatief: klant zit boven de maatgrens → past niet (tenzij stretch)
  function scoreBodyMeasurement(diff, stretch, pref) {
    const tolerance = STRETCH_TOLERANCE[stretch] ?? 0;
    const effectiveDiff = diff + tolerance; // stretch vergroot de tolerantie
    if (effectiveDiff < 0)  return effectiveDiff * 25; // te krap, penalty (proportioneel — minder negatief = betere keuze)

    // Ideale ruimte: fitted=0, normal=2, loose=6
    const ideal = pref === 'fitted' ? 0 : pref === 'loose' ? 6 : 2;
    return Math.max(100 - Math.abs(ideal - diff) * 10, -20);
  }

  // ── Brand correction ──────────────────────────────────────────────────────
  function getBrandCorrection(chart, gender) {
    const std = GBT_CHEST[gender] ?? GBT_CHEST.women;
    const devs = [];
    for (const [lbl, g] of Object.entries(chart)) {
      if (META_KEYS.has(lbl) || typeof g !== 'object') continue;
      const gc = g.chest_cm || g.bust_cm;
      const sc = std[lbl.toUpperCase()] || std[lbl];
      if (gc && sc) devs.push(gc - sc);
    }
    if (!devs.length) return 0;
    return Math.round(devs.reduce((a, b) => a + b, 0) / devs.length);
  }

  // ── Maat aanbevelen ───────────────────────────────────────────────────────
  function recommendSize(body, chart, stretch, pref) {
    const sizes = [];
    for (const [lbl, garment] of Object.entries(chart)) {
      if (META_KEYS.has(lbl) || typeof garment !== 'object') continue;
      const eases = {};
      let worst = Infinity;
      for (const [key, gval] of Object.entries(garment)) {
        if (!SCORABLE.has(key)) continue;
        const bval = body[BODY_KEY[key]];
        if (bval == null) continue;
        const diff = gval - bval; // chart - lichaam (positief = ruimte)
        const sc = scoreBodyMeasurement(diff, stretch, pref);
        eases[key] = { diff, score: sc };
        if (sc < worst) worst = sc;
      }
      sizes.push({ label: lbl, garment, eases, score: worst === Infinity ? 50 : worst });
    }
    return sizes.sort((a, b) => b.score - a.score);
  }

  // ── Internationale maat ophalen ───────────────────────────────────────────
  function getIntlSize(bodyChest, gender) {
    const ref = INT_SIZES[gender === 'men' ? 'men' : 'women'];
    const field = gender === 'men' ? 'chest' : 'bust';
    const m = ref.find(r => bodyChest <= r[field] + 2) || ref[ref.length - 1];
    return gender === 'men'
      ? `EU ${m.eu} / UK ${m.uk} / US ${m.us}`
      : `EU ${m.eu} / UK ${m.uk} / US ${m.us} / FR ${m.fr} / IT ${m.it}`;
  }

  // ── Pasvorm-label (op basis van diff = chart - lichaam) ──────────────────
  function easeLabel(diff) {
    if (diff < -2)  return { text: T.tooSmall || 'Likely too small', cls: 'sc-bad' };
    if (diff < 0)   return { text: T.verySnug || 'Very snug (stretch helps)', cls: 'sc-warn' };
    if (diff < 2)   return { text: T.snug || 'Snug / fitted', cls: 'sc-ok' };
    if (diff < 6)   return { text: T.comfort || 'Comfortable fit', cls: 'sc-good' };
    if (diff < 12)  return { text: T.relaxed || 'Relaxed fit', cls: 'sc-ok' };
    return { text: T.oversized || 'Oversized', cls: 'sc-warn' };
  }

  // ── Resultaat HTML renderen ───────────────────────────────────────────────
  function renderResult(sizes, chart, body, correction) {
    if (!sizes.length) return '<p>No recommendation available.</p>';
    const best = sizes[0];
    const gender = chart.gender || 'women';
    const bodyChest = body.chest;
    const intl = bodyChest ? getIntlSize(bodyChest, gender) : null;

    let html = `<div class="sc-rec-badge">`;
    html += `<span class="sc-rec-check">✓</span> <strong>Recommended: Size ${best.label}</strong>`;
    html += `</div>`;

    for (const [key, { diff }] of Object.entries(best.eases)) {
      const lbl = LABEL_MAP[key] || key;
      const el = easeLabel(diff);
      html += `<div class="sc-ease-row ${el.cls}"><span>${lbl}:</span> ${el.text}</div>`;
    }

    // Brand correction is used internally for scoring accuracy
    // but not shown to the customer

    if (intl) {
      html += `<div class="sc-intl">Your international size: <strong>${intl}</strong></div>`;
    }

    if (sizes.length > 1) {
      html += `<details class="sc-other-sizes"><summary>Compare other sizes</summary>`;
      for (const sz of sizes.slice(1)) {
        html += `<div class="sc-other-size"><strong>${sz.label}:</strong> `;
        const parts = Object.entries(sz.eases).map(([k, { diff }]) => {
          const lbl = LABEL_MAP[k] || k;
          const el = easeLabel(diff);
          return `${lbl} ${el.text}`;
        });
        html += parts.join(', ') || '—';
        html += `</div>`;
      }
      html += `</details>`;
    }

    return html;
  }

  // ── Tabel renderen ────────────────────────────────────────────────────────
  function renderTable(chart, unit, extrapolatedSet) {
    // Voetlengte en Amerikaanse maat worden in stilenza-product.liquid berekend en
    // niet uit het metafield gelezen — zie de toelichting daar. Hier overschrijven
    // we de waarden uit de data ermee, zodat de tabel exact toont wat er onder de
    // maatknop staat. Vorm: |41=26.0|42=26.7
    const kaart = (bron) => {
      const uit = {};
      for (const deel of (bron || '').split('|')) {
        const [maat, waarde] = deel.split('=');
        if (maat && waarde) uit[maat] = waarde;
      }
      return uit;
    };
    const usMap = kaart(window.__scUsMap__);
    const cmMap = kaart(window.__scCmMap__);

    const sizes = [];
    const allKeys = new Set();
    for (const [lbl, g] of Object.entries(chart)) {
      if (META_KEYS.has(lbl) || typeof g !== 'object') continue;
      const rij = { ...g };
      if (usMap[lbl]) rij.us = usMap[lbl];
      // alleen bij schoenen: cmMap is leeg voor kleding, en dan blijft de
      // borst/taille-data van het metafield onaangeroerd
      if (cmMap[lbl] && 'foot_length_cm' in g) rij.foot_length_cm = cmMap[lbl];
      sizes.push([lbl, rij]);
      Object.keys(rij).forEach(k => allKeys.add(k));
    }
    // Sort by canonical size order (XS → S → M → L → XL → ...)
    sizes.sort((a, b) => sizeIndex(a[0]) - sizeIndex(b[0]));

    const ORDER = ['foot_length_cm','insole_cm','chest_cm','bust_cm','waist_cm','hip_cm','shoulder_cm','length_cm','sleeve_cm','heel_cm','eu','uk','us'];

    // De us-kolom alleen in de Amerikaanse markt. Elders voegt hij niets toe en
    // nodigt hij uit tot verkeerd omrekenen — heren en dames hebben bij dezelfde
    // EU-maat een ander Amerikaans label. window.__scAltField__ komt uit
    // stilenza-product.liquid en is hetzelfde veld dat onder de maatknoppen staat,
    // zodat tabel en knoppen niet uiteenlopen.
    const cols = ORDER.filter(k =>
      allKeys.has(k) && (k !== 'us' || window.__scAltField__ === 'us'));

    // Ook de eerste kolomkop uit de labels halen. Die stond als enige nog
    // hard in het Engels, waardoor een Japanse maattabel "Size" boven de
    // maten had staan en 身幅 en 着丈 ernaast.
    let thead = '<tr><th>' + (T.size || 'Size') + '</th>';
    for (const col of cols) {
      thead += `<th>${LABEL_MAP[col] || col}</th>`;
    }
    thead += '</tr>';

    let tbody = '';
    let hasExtrapolated = false;
    for (const [lbl, g] of sizes) {
      const isExtra = extrapolatedSet && extrapolatedSet.has(lbl);
      if (isExtra) hasExtrapolated = true;
      tbody += `<tr${isExtra ? ' class="sc-extrapolated-row"' : ''}>`;
      tbody += `<td><strong>${lbl}${isExtra ? '*' : ''}</strong></td>`;
      for (const col of cols) {
        const val = g[col];
        if (val == null) { tbody += '<td>—</td>'; continue; }
        let display;
        if (col.endsWith('_cm')) {
          // length measurements — respect cm/inch unit toggle
          display = unit === 'inch' ? (val / 2.54).toFixed(1) + '"' : val + 'cm';
        } else {
          // shoe conversions (EU/UK/US) — plain numbers, no unit
          display = val;
        }
        tbody += `<td>${display}</td>`;
      }
      tbody += '</tr>';
    }

    return { thead, tbody, hasExtrapolated };
  }

  // ── Veld-configuratie per kledingcategorie ───────────────────────────────
  // chest/waist/hip/length: 'required' | 'optional' | 'hidden'
  // hideAdvisor: true = geen find-my-size tab (accessoires, schoenen)
  const CATEGORY_CONFIG = {
    top:       { chest: 'required', waist: 'optional', hip: 'hidden',   length: 'hidden'   },
    outerwear: { chest: 'required', waist: 'optional', hip: 'hidden',   length: 'hidden'   },
    bottom:    { chest: 'hidden',   waist: 'required', hip: 'required', length: 'optional', lengthLabel: 'Inseam (cm)' },
    dress:     { chest: 'required', waist: 'optional', hip: 'optional', length: 'optional', lengthLabel: 'Height (cm)' },
    set:       { chest: 'required', waist: 'optional', hip: 'optional', length: 'hidden'   },
    homewear:  { chest: 'required', waist: 'optional', hip: 'optional', length: 'hidden'   },
    footwear:  { hideAdvisor: true },
    accessory: { hideAdvisor: true },
  };
  // Aliassen voor wat er in het metafield staat
  const CATEGORY_ALIAS = {
    tops: 'top', shirt: 'top', blouse: 'top', sweater: 'top', hoodie: 'top',
    jacket: 'outerwear', coat: 'outerwear', blazer: 'outerwear', cardigan: 'outerwear',
    pants: 'bottom', trousers: 'bottom', skirt: 'bottom', shorts: 'bottom', jeans: 'bottom',
    dresses: 'dress', mini: 'dress', midi: 'dress', maxi: 'dress',
    sets: 'set', suit: 'set', jumpsuit: 'set',
    shoes: 'footwear', boots: 'footwear', heels: 'footwear', sandals: 'footwear', sneakers: 'footwear',
    jewelry: 'accessory', accessories: 'accessory', bags: 'accessory', belts: 'accessory', watches: 'accessory',
  };

  function configureFields(cfg) {
    function setField(id, state, labelOverride) {
      var field = document.getElementById('sc-field-' + id);
      if (!field) return;
      if (state === 'hidden') {
        field.style.display = 'none';
        return;
      }
      field.style.display = '';
      var optionalSpan = field.querySelector('.sc-optional');
      if (optionalSpan) optionalSpan.style.display = state === 'required' ? 'none' : '';
      if (labelOverride) {
        var lbl = document.getElementById('sc-label-' + id);
        if (lbl) lbl.firstChild.textContent = labelOverride + ' ';
      }
    }
    setField('chest', cfg.chest || 'required');
    setField('waist', cfg.waist || 'optional');
    setField('hip',   cfg.hip   || 'optional');
    setField('length', cfg.length || 'hidden', cfg.lengthLabel);
  }

  // ── Modal initialiseren ────────────────────────────────────────────────────
  function init() {
    const chart = window.__sizeChart__;
    if (!chart) return;

    // Extrapolate missing sizes from product variants
    const extrapolatedSizes = extrapolateChart(chart, window.__productSizes__ || []);

    const modal    = document.getElementById('size-chart-modal');
    const openBtn  = document.getElementById('size-chart-open');
    const closeBtn = document.getElementById('sc-close');
    const backdrop = document.getElementById('sc-backdrop');
    if (!modal || !openBtn) return;

    const rawCat   = (chart.garment_category || 'top').toLowerCase();
    const category = CATEGORY_ALIAS[rawCat] || rawCat;
    const cfg      = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.top;
    const stretch  = chart.fabric_stretch   || 'none';
    const gender   = chart.gender           || 'women';
    const correction = getBrandCorrection(chart, gender);

    // Verberg advisor voor accessoires/schoenen of producten zonder scorable maten
    const hasScorableMeasurements = !cfg.hideAdvisor && Object.entries(chart).some(([lbl, g]) =>
      !META_KEYS.has(lbl) && typeof g === 'object' &&
      Object.keys(g).some(k => SCORABLE.has(k))
    );
    if (!hasScorableMeasurements) {
      document.querySelector('.sc-tab[data-tab="advisor"]').style.display = 'none';
      document.getElementById('sc-panel-advisor').hidden = true;
      document.querySelector('.sc-tab[data-tab="table"]').classList.add('active');
      document.getElementById('sc-panel-table').hidden = false;
    } else {
      configureFields(cfg);
    }

    // Primary required field: 'chest' voor tops/dresses, 'waist' voor bottoms
    const primaryField = cfg.chest === 'required' ? 'chest' : 'waist';
    const primaryLabel = primaryField === 'waist' ? 'waist' : 'bust / chest';

    let unit = localStorage.getItem('sc_unit') || 'cm';
    let pref = 'normal';

    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved) {
      if (saved.chest)  document.getElementById('sc-chest').value  = saved.chest;
      if (saved.waist)  document.getElementById('sc-waist').value  = saved.waist;
      if (saved.hip)    document.getElementById('sc-hip').value    = saved.hip;
      if (saved.length) document.getElementById('sc-length').value = saved.length;
    }

    openBtn.addEventListener('click', () => {
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      if (saved) calculateAndShow();
      refreshTable();
    });

    function closeModal() {
      modal.hidden = true;
      document.body.style.overflow = '';
    }
    closeBtn.addEventListener('click', closeModal);
    backdrop.addEventListener('click', closeModal);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeModal(); });

    document.querySelectorAll('.sc-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        document.querySelectorAll('.sc-tab').forEach(t => t.classList.remove('active'));
        document.querySelectorAll('.sc-panel').forEach(p => p.hidden = true);
        tab.classList.add('active');
        document.getElementById(`sc-panel-${tab.dataset.tab}`).hidden = false;
        if (tab.dataset.tab === 'table') refreshTable();
      });
    });

    document.querySelectorAll('.sc-pref-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.sc-pref-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        pref = btn.dataset.pref;
      });
    });

    document.getElementById('sc-calculate').addEventListener('click', calculateAndShow);

    function calculateAndShow() {
      const chest  = parseFloat(document.getElementById('sc-chest').value)  || null;
      const waist  = parseFloat(document.getElementById('sc-waist').value)  || null;
      const hip    = parseFloat(document.getElementById('sc-hip').value)    || null;
      const length = parseFloat(document.getElementById('sc-length').value) || null;

      const primary = primaryField === 'waist' ? waist : chest;
      if (!primary) {
        const res = document.getElementById('sc-result');
        res.hidden = false;
        res.innerHTML = '<p class="sc-error">Please enter your ' + primaryLabel + ' measurement to continue.</p>';
        return;
      }
      const body  = { chest, waist, hip, length };
      const sizes = recommendSize(body, chart, stretch, pref);
      const html  = renderResult(sizes, chart, body, correction);
      const res   = document.getElementById('sc-result');
      res.hidden  = false;
      res.innerHTML = html;

      if (sizes.length) refreshTable(sizes[0].label);

      if (document.getElementById('sc-save-checkbox').checked) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(body));
      }
    }

    document.querySelectorAll('.sc-unit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.sc-unit-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        unit = btn.dataset.unit;
        localStorage.setItem('sc_unit', unit);
        refreshTable();
      });
    });

    function refreshTable(highlightSize) {
      const { thead, tbody, hasExtrapolated } = renderTable(chart, unit, extrapolatedSizes);
      document.getElementById('sc-thead').innerHTML = thead;
      document.getElementById('sc-tbody').innerHTML = tbody;
      // Show/hide extrapolation footnote
      const noteEl = document.getElementById('sc-extrapolated-note');
      if (noteEl) noteEl.hidden = !hasExtrapolated;
      if (highlightSize) {
        document.querySelectorAll('#sc-tbody tr').forEach(row => {
          const cellText = row.cells[0]?.textContent.trim().replace('*', '');
          if (cellText === highlightSize) {
            row.classList.add('sc-recommended-row');
          }
        });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
